// Horse profile card. Mirrors data-component="horse-profile" from the
// approved HTML. Portrait on the left, identity + stat grid + story on
// the right. Portrait sourced from services/horse-timeline portrait
// (proposed - TODO runtime) or the Drive "01-product-jugs" library.
//
// TODO(runtime): hydrate portrait + profile from services/horse-timeline
// summarize call (confirmed service, no HTTP read endpoint yet).
//
// W32 (2026-10-20): `customerName` / `customerFirstName` / `gender` are
// optional so a real-contact screen can pass the HubSpot owner and the
// horse_gender_N value. Omitted, the card keeps the approved design copy.
import type { ReactNode } from 'react';
import type { HorseProfile as HorseProfileData, EditorMode } from '../mockData.js';

export interface HorseProfileProps {
  horse: HorseProfileData;
  onEdit: (mode: EditorMode) => void;
  /** Customer cell. Defaults to the design-preview customer. */
  customerName?: string;
  /** First name used in the empty-story prompt. Defaults to the design-preview customer. */
  customerFirstName?: string;
  /** HubSpot horse gender when known; renders an extra stat cell. */
  gender?: string | null;
}

export function HorseProfile({ horse, onEdit, customerName = 'Sarah Mitchell', customerFirstName = 'Sarah', gender }: HorseProfileProps): ReactNode {
  const emptyStory = !horse.storyText || horse.storyText.length === 0;
  return (
    <section className="card horse-profile" data-component="horse-profile">
      <div className="horse-profile-portrait">
        {horse.portraitUrl && <img src={horse.portraitUrl} alt={`Illustrative horse portrait for ${horse.name}`} />}
        <span>{horse.portraitUrl ? 'Sample photo' : 'Photo not provided'}</span>
      </div>
      <div className="horse-profile-body">
        <div className="row">
          <span className="active-pill">{horse.horseStatus}</span>
          <button className="text-btn" type="button" onClick={() => onEdit('profile')}>
            Edit horse
          </button>
        </div>
        <h2>{horse.name}</h2>
        <p className="horse-subtitle">
          {horse.breed} &middot; {horse.discipline}
        </p>
        <div className="horse-stat-grid">
          <div>
            <span>Reported age</span>
            <strong>{horse.reportedAge}</strong>
          </div>
          <div>
            <span>Birth date</span>
            <strong className={horse.birthDate === 'Not recorded' ? 'missing' : undefined}>
              {horse.birthDate}
            </strong>
          </div>
          <div>
            <span>Customer</span>
            <strong>{customerName}</strong>
          </div>
          <div>
            <span>Horse status</span>
            <strong>{horse.horseStatus}</strong>
          </div>
          {gender ? (
            <div>
              <span>Gender</span>
              <strong data-testid="horse-gender">{gender}</strong>
            </div>
          ) : null}
        </div>
        <div className="horse-story">
          <div className="story-heading">
            <span className="eyebrow">{horse.name.toUpperCase()}'S STORY</span>
            <button className="text-btn" type="button" onClick={() => onEdit('story')}>
              Edit story
            </button>
          </div>
          <p className={emptyStory ? 'story-empty' : undefined}>
            {emptyStory
              ? `How did ${customerFirstName} and ${horse.name} find each other? Capture the horse's personality, their favorite moments and what makes this horse special.`
              : horse.storyText}
          </p>
          <small>{horse.storyAuthorNote}</small>
        </div>
      </div>
    </section>
  );
}
